import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import dayjs from "dayjs";
import {
  rentPaymentFieldConfiguration,
  rentPaymentInitialValues,
} from "../../../formConfigs/Fleet/rentPaymentConfig";
import { fetchRents } from "../RentPaymentPopup/RentPaymentPopup";

interface UpdateLoanPaymentPopupProps {
  rentPayment: any;
}

const UpdateRentPaymentPopup = ({
  rentPayment,
}: UpdateLoanPaymentPopupProps) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(rentPaymentInitialValues);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: rentData } = useQuery({
    queryKey: ["rents"],
    queryFn: () => fetchRents(token),
    enabled: !!open,
  });

  useEffect(() => {
    if (rentPayment) {
      setInitialValues({
        ...rentPaymentInitialValues,
        ...rentPayment,
        rentId:
          rentData?.find((l: any) => l.id === rentPayment.rentId) ||
          rentPayment.rentId,
        paymentDate: dayjs(rentPayment.paymentDate).format("YYYY-MM-DD"),
      });
    }
  }, [rentPayment, rentData]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/rentPayment`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rentPayments"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update Rent payment");
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      rentId: getId(data.rentId),
    };
    updateMutation.mutate(finalData);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={(e) => setOpen(true)}
      >
        Update
      </Button>
      <GenericFormPopup
        formFields={rentPaymentFieldConfiguration}
        initialValues={initialValues}
        title="Update Rent Payment"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{ loans: rentData }}
        loading={updateMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Rent Payment updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateRentPaymentPopup;
